// Section 2 — Partner Storefront Setup
// Lets a third-party partner (store / brand / company) configure the
// public-facing pieces of their storefront:
//   • Logo upload (via /api/uploads)
//   • Public business name + bio
//   • Address + city/state/ZIP
//   • Map coordinates (manual lat/lng entry — full geocoder lands later)
//   • Website
// Saves through PUT /api/partner/me/profile. Does NOT mutate compliance —
// the partner can keep editing until admin approves them.
import React, { useState } from "react";
import { MapPin, Globe, Image as ImageIcon, Save, ExternalLink, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

export default function PartnerStorefrontSetup({ profile, onSaved }) {
  const [form, setForm] = useState({
    business_name: profile?.business_name || "",
    bio: profile?.bio || "",
    website: profile?.website || "",
    primary_address: profile?.primary_address || "",
    city: profile?.city || "",
    state: profile?.state || "",
    zip_code: profile?.zip_code || "",
    lat: profile?.lat ?? "",
    lng: profile?.lng ?? "",
    logo_url: profile?.logo_url || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [uploading, setUploading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      // Iter76 — convert to data: URI on the client, then send to the
      // hybrid /api/partner/me/logo endpoint. The server tries Cloudinary
      // first, then falls back to inline DB storage so the image NEVER
      // disappears (survives refresh, logout, and deploy).
      const dataUri = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/partner/me/logo", { image: dataUri });
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      setOkMsg(
        data.storage === "cloudinary"
          ? "Logo uploaded to Cloudinary and saved."
          : "Logo saved (inline DB storage). It will display everywhere immediately.",
      );
      if (onSaved) await onSaved();
    } catch (ex) {
      setErr(apiErrorMessage(ex));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    setOkMsg("");
    try {
      const payload = {
        business_name: form.business_name || null,
        bio: form.bio || null,
        website: form.website || null,
        primary_address: form.primary_address || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        lat: form.lat === "" ? null : Number(form.lat),
        lng: form.lng === "" ? null : Number(form.lng),
        logo_url: form.logo_url || null,
      };
      const { data } = await api.put("/partner/me/profile", payload);
      setOkMsg("Saved. Public storefront updated.");
      if (onSaved) onSaved(data);
    } catch (ex) {
      setErr(apiErrorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  const isApproved = (profile?.compliance?.status === "approved");
  const publicUrl = profile?.slug ? `/shop/partner/${profile.slug}` : null;

  return (
    <section className="glass-panel p-6" data-testid="partner-storefront-setup">
      <header className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2">
            <ImageIcon className="h-3 w-3" /> Storefront setup
          </div>
          <h2 className="font-anton text-xl uppercase text-white mt-1">Public profile</h2>
          <p className="text-xs text-lifted-dim mt-1 max-w-md">
            This is what customers see on your dedicated Lifted shop section.
            Goes live the moment Lifted admin approves your partner profile.
          </p>
        </div>
        {publicUrl && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-lifted-green inline-flex items-center gap-1 hover:underline"
            data-testid="partner-public-link"
          >
            <ExternalLink size={11} /> {isApproved ? "View live page" : "Preview URL"}
          </a>
        )}
      </header>

      <form onSubmit={save} className="space-y-4">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-md bg-white/5 overflow-hidden grid place-items-center shrink-0">
            {form.logo_url ? (
              <img src={form.logo_url} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="text-lifted-dim" size={20} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <label className="block text-xs uppercase tracking-wider text-lifted-dim mb-1">
              Logo
            </label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onLogoChange}
              data-testid="partner-logo-upload"
              className="text-xs text-lifted-dim"
            />
            {uploading && (
              <div className="text-xs text-lifted-dim mt-1 inline-flex items-center gap-1">
                <Loader2 className="animate-spin" size={11} /> Uploading…
              </div>
            )}
          </div>
        </div>

        <FormRow label="Business name" required>
          <input
            type="text"
            value={form.business_name}
            onChange={set("business_name")}
            className="input-lifted"
            data-testid="partner-business-name"
            required
          />
        </FormRow>

        <FormRow label="Short bio">
          <textarea
            value={form.bio}
            onChange={set("bio")}
            rows={3}
            maxLength={600}
            className="input-lifted"
            data-testid="partner-bio"
          />
        </FormRow>

        <FormRow label="Website">
          <div className="flex items-center gap-2">
            <Globe className="text-lifted-dim" size={14} />
            <input
              type="url"
              value={form.website}
              onChange={set("website")}
              placeholder="https://"
              className="input-lifted flex-1"
              data-testid="partner-website-input"
            />
          </div>
        </FormRow>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormRow label="Street address">
            <input
              type="text"
              value={form.primary_address}
              onChange={set("primary_address")}
              className="input-lifted"
              data-testid="partner-address-input"
            />
          </FormRow>
          <FormRow label="City">
            <input
              type="text"
              value={form.city}
              onChange={set("city")}
              className="input-lifted"
              data-testid="partner-city-input"
            />
          </FormRow>
          <FormRow label="State">
            <input
              type="text"
              maxLength={2}
              value={form.state}
              onChange={set("state")}
              className="input-lifted uppercase"
              data-testid="partner-state-input"
            />
          </FormRow>
          <FormRow label="ZIP code">
            <input
              type="text"
              maxLength={10}
              value={form.zip_code}
              onChange={set("zip_code")}
              className="input-lifted"
              data-testid="partner-zip-input"
            />
          </FormRow>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormRow label="Map latitude">
            <input
              type="number"
              step="any"
              value={form.lat}
              onChange={set("lat")}
              placeholder="61.2181"
              className="input-lifted"
              data-testid="partner-lat-input"
            />
          </FormRow>
          <FormRow label="Map longitude">
            <input
              type="number"
              step="any"
              value={form.lng}
              onChange={set("lng")}
              placeholder="-149.9003"
              className="input-lifted"
              data-testid="partner-lng-input"
            />
          </FormRow>
        </div>
        <p className="text-[11px] text-lifted-dim inline-flex items-center gap-1">
          <MapPin size={11} />
          Lat/lng pin your shop on the live Lifted partner map. Look up coordinates from Google Maps.
        </p>

        {err && (
          <div className="text-sm text-rose-300 bg-rose-900/20 border border-rose-500/30 px-3 py-2 rounded" data-testid="partner-setup-error">
            {err}
          </div>
        )}
        {okMsg && (
          <div className="text-sm text-lifted-green bg-lifted-green/10 border border-lifted-green/30 px-3 py-2 rounded" data-testid="partner-setup-success">
            {okMsg}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="btn-lifted inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="partner-setup-save"
        >
          <Save size={14} />
          {busy ? "Saving…" : "Save storefront"}
        </button>
      </form>
    </section>
  );
}

function FormRow({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-lifted-dim mb-1">
        {label}
        {required && <span className="text-lifted-green ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
